import {
  ACTION_KEYS,
  ACTION_LABELS,
  actionApplies,
  AREA_SCOPE_LABELS,
  AREA_SCOPES,
  MODULE_KEYS,
  MODULE_LABELS,
  MODULE_VIEW_DEPENDENCIES,
  organizationPermissionsSchema,
  rolePermissionsSchema,
  type ActionKey,
  type AreaScopeName,
  type ModuleKey,
  type PermissionMatrix,
  type RolePermissions,
} from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Lock, ShieldCheck, ShieldOff } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormAlert } from '@/components/ui/form';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

type Draft = { areaScope: AreaScopeName; permissions: PermissionMatrix };

const AREA_SCOPE_HINTS: Record<AreaScopeName, string> = {
  ALL: 'Vê e trabalha nos itens de todas as áreas e unidades da empresa.',
  OWN: 'Só vê os itens das áreas e unidades ligadas à pessoa (e das que estão abaixo delas).',
};

/** Módulos que só têm efeito para um dos públicos (as telas e rotas deles são restritas). */
const FLUXUS_ONLY: ModuleKey[] = ['central-empresas'];
const CLIENT_ONLY: ModuleKey[] = ['visao-geral'];

const sameActions = (a: ActionKey[] = [], b: ActionKey[] = []) =>
  a.length === b.length && a.every((x) => b.includes(x));

function isDirty(role: RolePermissions, draft: Draft | undefined): boolean {
  if (!draft) return false;
  if (draft.areaScope !== role.areaScope) return true;
  return MODULE_KEYS.some((m) => !sameActions(role.permissions[m], draft.permissions[m]));
}

/** O que o perfil perde com a alteração (para a confirmação antes de salvar). */
function reductions(role: RolePermissions, draft: Draft): string[] {
  const out: string[] = [];
  if (role.areaScope === 'ALL' && draft.areaScope === 'OWN') {
    out.push('passa a ver só as próprias áreas e unidades');
  }
  for (const m of MODULE_KEYS) {
    const lost = (role.permissions[m] ?? []).filter((a) => !(draft.permissions[m] ?? []).includes(a));
    if (lost.length) {
      out.push(
        lost.includes('view')
          ? `perde o acesso a ${MODULE_LABELS[m]}`
          : `perde ${lost.map((a) => ACTION_LABELS[a]).join(', ')} em ${MODULE_LABELS[m]}`,
      );
    }
  }
  return out;
}

/** Marca ou desmarca uma ação: "visualizar" é pré-requisito das demais (mesma regra da API). */
function toggle(
  matrix: PermissionMatrix,
  module: ModuleKey,
  action: ActionKey,
  on: boolean,
): PermissionMatrix {
  const current = new Set(matrix[module] ?? []);
  if (on) {
    current.add(action);
    current.add('view');
  } else if (action === 'view') {
    current.clear();
  } else {
    current.delete(action);
  }
  const next = { ...matrix };
  const ordered = ACTION_KEYS.filter((a) => current.has(a));
  if (ordered.length) next[module] = ordered;
  else delete next[module];
  // Dependências entre módulos (mesma regra da API — T-280; DEC-087): a análise crítica traz "visualizar" em
  // Reuniões; tirar o acesso a Reuniões tira também a análise crítica.
  for (const [dependent, required] of Object.entries(MODULE_VIEW_DEPENDENCIES) as [ModuleKey, ModuleKey][]) {
    if (!next[dependent]) continue;
    if (module === required && !next[required]) delete next[dependent];
    else if (!next[required]?.includes('view')) next[required] = ['view', ...(next[required] ?? [])];
  }
  return next;
}

/**
 * Editor da matriz de permissões de uma organização (T-026d, RN-24): perfis × módulos × ações e
 * escopo por área, com a API da T-024. Serve às empresas clientes e à equipe Fluxus (`team`).
 */
export function PermissionsPanel({
  organizationId,
  team = false,
}: {
  organizationId: string;
  team?: boolean;
}) {
  const { client } = useAuth();
  const can = useCan();
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const key = ['permissions', organizationId];
  const [selected, setSelected] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const ids = { reason: useId(), scope: useId(), tabs: useId() };

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/permissions`, {
        schema: organizationPermissionsSchema,
        signal,
      }),
  });

  if (query.isPending)
    return <LoadingState rows={8} label="Carregando perfis e permissões" className="mt-6" />;
  if (query.isError) {
    return (
      <ErrorState
        className="mt-6"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }

  const roles = query.data.roles;
  const role = roles.find((r) => r.id === selected) ?? roles[0];
  if (!role) {
    return (
      <EmptyState
        className="mt-6 rounded-lg border border-dashed border-border py-10"
        icon={ShieldOff}
        title="Nenhum perfil nesta organização"
        description="Os perfis padrão são criados junto com a empresa. Se esta lista está vazia, fale com o suporte da Fluxus."
      />
    );
  }
  const canAdmin = can('administracao', 'admin');
  const isOwnRole = me.organization.id === organizationId && me.role.key === role.key;
  const readOnly = role.locked || isOwnRole || !canAdmin || saving;
  const draft = drafts[role.id] ?? { areaScope: role.areaScope, permissions: role.permissions };
  const changed = roles.filter((r) => isDirty(r, drafts[r.id]));

  const update = (next: Draft) => setDrafts((d) => ({ ...d, [role.id]: next }));

  async function save() {
    setConfirming(null);
    setSaving(true);
    setSaveError(null);
    const saved: string[] = [];
    try {
      for (const r of changed) {
        const d = drafts[r.id]!;
        const result = await client.request(`/organizations/${organizationId}/roles/${r.id}/permissions`, {
          method: 'PUT',
          body: { areaScope: d.areaScope, permissions: d.permissions, reason: reason.trim() || undefined },
          schema: rolePermissionsSchema,
        });
        saved.push(r.id);
        queryClient.setQueryData(key, (old: typeof query.data) =>
          old ? { ...old, roles: old.roles.map((x) => (x.id === result.id ? result : x)) } : old,
        );
        setDrafts((all) => {
          const rest = { ...all };
          delete rest[r.id];
          return rest;
        });
      }
      setReason('');
      toast.success(changed.length === 1 ? `Permissões de ${changed[0]!.name} salvas` : 'Permissões salvas', {
        description: 'Valem a partir da próxima ação de cada pessoa, sem precisar entrar de novo.',
      });
    } catch (err) {
      setSaveError(err);
      if (saved.length) {
        toast.error('Parte das alterações foi salva', {
          description: `Salvos: ${roles
            .filter((r) => saved.includes(r.id))
            .map((r) => r.name)
            .join(', ')}. ${errorMessage(err)}`,
        });
      }
    } finally {
      setSaving(false);
    }
  }

  function requestSave() {
    const lost = changed.flatMap((r) => reductions(r, drafts[r.id]!).map((line) => `${r.name}: ${line}`));
    if (lost.length) setConfirming(lost);
    else void save();
  }

  return (
    <section aria-label="Perfis e permissões" className="mt-6 flex flex-col gap-5">
      {team ? null : (
        <div className="flex gap-3 rounded-lg bg-brand-soft p-4 text-sm text-foreground">
          <Info className="mt-0.5 size-4 shrink-0 text-brand-strong dark:text-brand" aria-hidden />
          <p>
            A estrutura da empresa, os usuários e esta matriz são administrados pela equipe Fluxus. Marcar{' '}
            <strong>{MODULE_LABELS.administracao}</strong> num perfil desta empresa não dá a ele acesso a
            essas telas. Os consultores seguem os{' '}
            <Link to="/configuracoes/perfis" className="font-semibold underline underline-offset-2">
              perfis da equipe
            </Link>
            .
          </p>
        </div>
      )}

      <div
        role="tablist"
        aria-label="Perfis"
        className="flex gap-1 overflow-x-auto border-b border-border"
        onKeyDown={(e) => {
          // Padrão de abas: setas, Home e End trocam de perfil e levam o foco junto.
          const index = roles.findIndex((r) => r.id === role.id);
          const next =
            e.key === 'ArrowRight'
              ? (index + 1) % roles.length
              : e.key === 'ArrowLeft'
                ? (index - 1 + roles.length) % roles.length
                : e.key === 'Home'
                  ? 0
                  : e.key === 'End'
                    ? roles.length - 1
                    : -1;
          if (next < 0 || saving) return;
          e.preventDefault();
          setSelected(roles[next]!.id);
          document.getElementById(`${ids.tabs}-${roles[next]!.id}`)?.focus();
        }}
      >
        {roles.map((r) => {
          const active = r.id === role.id;
          const dirty = isDirty(r, drafts[r.id]);
          return (
            <button
              key={r.id}
              type="button"
              role="tab"
              id={`${ids.tabs}-${r.id}`}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              disabled={saving && !active}
              aria-label={dirty ? `${r.name} (alterado)` : undefined}
              aria-controls={`${ids.tabs}-painel`}
              onClick={() => setSelected(r.id)}
              className={cn(
                '-mb-px inline-flex h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm font-semibold transition-colors',
                active
                  ? 'border-brand text-brand-strong dark:text-brand'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {r.locked ? <Lock className="size-3.5" aria-hidden /> : null}
              {r.name}
              {dirty ? <span aria-hidden className="size-1.5 rounded-full bg-brand" /> : null}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`${ids.tabs}-painel`}
        aria-labelledby={`${ids.tabs}-${role.id}`}
        className="flex flex-col gap-5"
      >
        {role.locked ? (
          <FormAlert tone="locked" title="Perfil travado">
            O Administrador Fluxus sempre tem acesso total, para a plataforma nunca ficar sem quem a
            administre.
          </FormAlert>
        ) : isOwnRole ? (
          <FormAlert tone="locked" title="Este é o seu perfil">
            Você não pode alterar as próprias permissões: peça a outro Administrador Fluxus.
          </FormAlert>
        ) : !canAdmin ? (
          <FormAlert tone="locked" title="Somente leitura">
            Seu perfil pode ver a matriz, mas não alterá-la.
          </FormAlert>
        ) : null}

        <fieldset aria-describedby={`${ids.scope}-dica`}>
          <legend className="text-sm font-bold">Alcance dos dados</legend>
          <p id={`${ids.scope}-dica`} className="mt-1 text-sm text-muted-foreground">
            Vale para objetivos, indicadores, metas, iniciativas e planos ligados a áreas e unidades.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {AREA_SCOPES.map((scope) => (
              <label
                key={scope}
                className={cn(
                  'flex cursor-pointer gap-3 rounded-lg border p-3 text-sm transition-colors',
                  draft.areaScope === scope
                    ? 'border-brand bg-brand-soft/60'
                    : 'border-border hover:bg-muted',
                  readOnly && 'cursor-default opacity-80',
                )}
              >
                <input
                  type="radio"
                  name={`${ids.scope}-${role.id}`}
                  className="mt-0.5 size-4 accent-(--brand)"
                  checked={draft.areaScope === scope}
                  disabled={readOnly}
                  onChange={() => update({ ...draft, areaScope: scope })}
                />
                <span>
                  <span className="block font-semibold">{AREA_SCOPE_LABELS[scope]}</span>
                  <span className="text-muted-foreground">{AREA_SCOPE_HINTS[scope]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <h2 className="text-sm font-bold">Módulos e ações</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            “Visualizar” é necessário para as demais ações: desmarcá-lo tira o acesso ao módulo.
          </p>
          {/* Rola na horizontal em telas estreitas: a região recebe o foco para rolar pelo teclado. "relative" para os textos
              só de leitor de tela (absolutos) ficarem dentro da rolagem e não alargarem a página. */}
          <div
            className="relative isolate mt-3 overflow-x-auto rounded-lg border border-border"
            role="region"
            aria-label={`Permissões de ${role.name}`}
            tabIndex={0}
          >
            <table className="w-full min-w-[44rem] border-collapse text-sm">
              <caption className="sr-only">Permissões de {role.name} por módulo e ação</caption>
              <thead>
                <tr className="bg-muted">
                  <th
                    scope="col"
                    className="sticky left-0 z-(--z-index-sticky) w-36 bg-muted px-3 py-2.5 text-left font-semibold sm:w-56"
                  >
                    Módulo
                  </th>
                  {ACTION_KEYS.map((a) => (
                    <th
                      key={a}
                      scope="col"
                      className="px-2 py-2.5 text-center text-xs font-semibold whitespace-nowrap"
                    >
                      {ACTION_LABELS[a]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULE_KEYS.map((m) => {
                  const actions = draft.permissions[m] ?? [];
                  const original = role.permissions[m] ?? [];
                  const note =
                    m === 'administracao' && !team
                      ? 'Não libera estrutura, usuários e matriz (equipe Fluxus)'
                      : FLUXUS_ONLY.includes(m) && !team
                        ? 'Só tem efeito para a equipe Fluxus'
                        : CLIENT_ONLY.includes(m) && team
                          ? 'Só tem efeito para empresas clientes'
                          : MODULE_VIEW_DEPENDENCIES[m]
                            ? `Inclui visualizar ${MODULE_LABELS[MODULE_VIEW_DEPENDENCIES[m]]}`
                            : null;
                  return (
                    <tr key={m} className="border-t border-border">
                      <th
                        scope="row"
                        className="sticky left-0 z-(--z-index-sticky) w-36 bg-surface-raised px-3 py-2 text-left text-xs font-medium sm:w-56 sm:text-sm"
                      >
                        {MODULE_LABELS[m]}
                        {note ? (
                          <span className="block text-xs font-normal text-muted-foreground">{note}</span>
                        ) : null}
                      </th>
                      {ACTION_KEYS.map((a) => {
                        // Ação sem efeito no módulo (T-281; DEC-088): sem caixa — a API e o banco também recusam.
                        if (!actionApplies(m, a)) {
                          return (
                            <td key={a} className="px-2 py-2 text-center text-muted-foreground">
                              <span aria-hidden>—</span>
                              <span className="sr-only">{`${ACTION_LABELS[a]} não se aplica a ${MODULE_LABELS[m]}`}</span>
                            </td>
                          );
                        }
                        const on = actions.includes(a);
                        const was = original.includes(a);
                        return (
                          <td key={a} className={cn('px-2 py-2 text-center', on !== was && 'bg-brand-soft')}>
                            <input
                              type="checkbox"
                              className="size-4 scroll-ml-40 accent-(--brand) disabled:cursor-not-allowed sm:scroll-ml-60"
                              aria-label={`${ACTION_LABELS[a]} em ${MODULE_LABELS[m]}`}
                              checked={on}
                              disabled={readOnly}
                              onChange={(e) =>
                                update({
                                  ...draft,
                                  permissions: toggle(draft.permissions, m, a, e.target.checked),
                                })
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {canAdmin ? (
        <div
          className={cn(
            'flex flex-col gap-3 border-t border-border bg-background/95 py-4 sm:rounded-lg sm:border sm:px-4',
            // Fixa no rodapé só enquanto há o que salvar (sem alterações, não cobre a matriz).
            changed.length > 0 && 'sticky bottom-0 z-(--z-index-sticky) -mx-4 px-4 backdrop-blur-sm sm:mx-0',
          )}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor={ids.reason} className="text-sm font-medium">
              Justificativa{' '}
              <span className="font-normal text-muted-foreground">(opcional, vai para o histórico)</span>
            </label>
            <textarea
              id={ids.reason}
              rows={2}
              maxLength={1000}
              value={reason}
              disabled={!changed.length || saving}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: gerentes passam a aprovar metas a partir do 2º semestre"
              className="w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm text-foreground shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
          {saveError ? <FormAlert tone="error">{errorMessage(saveError)}</FormAlert> : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {changed.length === 0
                ? 'Nenhuma alteração.'
                : changed.length === 1
                  ? `Alterações em ${changed[0]!.name}.`
                  : `Alterações em ${changed.length} perfis: ${changed.map((r) => r.name).join(', ')}.`}
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                variant="outline"
                disabled={!changed.length || saving}
                onClick={() => {
                  setDrafts({});
                  setReason('');
                  setSaveError(null);
                }}
              >
                Descartar
              </Button>
              <Button disabled={!changed.length} loading={saving} onClick={requestSave}>
                <ShieldCheck aria-hidden />
                Salvar alterações
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        title="Reduzir permissões?"
        description={
          <>
            <span className="block">Quem tem estes perfis perde acesso na próxima ação, sem aviso:</span>
            <ul className="mt-2 list-disc pl-5">
              {(confirming ?? []).slice(0, 8).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {(confirming?.length ?? 0) > 8 ? (
              <span className="mt-1 block">e mais {confirming!.length - 8} alterações.</span>
            ) : null}
          </>
        }
        confirmLabel="Salvar e reduzir"
        destructive
        busy={saving}
        onConfirm={() => void save()}
      />
    </section>
  );
}
